import { Controller } from '@hotwired/stimulus';
import Logger from '../lib/logger';
// Renders ```mermaid fences inside the Docs viewer (see DocsController, _doc_body.html.erb).
//
// Docs::MarkdownRenderer emits fenced code blocks as <pre><code class="language-mermaid">
// (kramdown's plain GFM output — syntax highlighting is disabled specifically so this stays
// unmangled). Mermaid's own run() API expects a bare <pre class="mermaid">, so this adapts the
// DOM once before mermaid ever touches it.
//
// Also gives each diagram the same zoom in/out/reset + fullscreen affordance as the topology
// presenter's own diagrams (see topology_controller.ts's zoomDiagram/openModal/zoomModal). The
// toolbar and fullscreen modal are built here directly in JS — rather than as data-action
// bindings on server-rendered markup, the way topology_controller.ts's do — because a doc's
// diagrams don't exist in the DOM until this controller wraps them itself; there's no ERB
// template to attach data-action to. Deliberately a separate, small controller rather than
// reusing topology_controller.ts, which owns unrelated tour/inspector/overlay state that has no
// meaning here.
//
// Unlike the topology presenter — a self-contained dark UI regardless of the app theme — this
// follows whichever theme the reader has chosen: Mermaid bakes its palette into the SVG at
// render time, so a live dark/light toggle (theme-toggle-controller flips documentElement's
// data-bs-theme) needs a full re-render from the stashed source, not just a CSS change. Mirrors
// flatpickr_controller.js's own MutationObserver approach to the same problem.
export default class extends Controller {
    constructor() {
        super(...arguments);
        this.mermaid = null;
        this.intersectionObserver = null;
        this.themeObserver = null;
        this.diagrams = [];
        this.zoomLevels = new Map();
        this.modalBackdrop = null;
        this.modalBody = null;
        this.modalScale = 1.0;
        this.fullscreenSource = null;
        this.handleEscape = (event) => {
            if (event.key === 'Escape')
                this.closeFullscreen();
        };
    }
    async connect() {
        this.diagrams = this.wrapDiagrams();
        if (this.diagrams.length === 0)
            return;
        try {
            const mermaidModule = await import('mermaid');
            this.mermaid = mermaidModule.default;
            this.initializeMermaid();
            // Observe each diagram individually, not the whole doc-content container — on a long
            // doc, 10% of the *container's* area can require scrolling several screens down even
            // when a diagram near the top is already fully visible, leaving it stuck as unrendered
            // source text until that scroll happens.
            this.intersectionObserver = new IntersectionObserver((entries) => {
                const nowVisible = entries.filter((entry) => entry.isIntersecting);
                if (nowVisible.length === 0)
                    return;
                nowVisible.forEach((entry) => this.intersectionObserver?.unobserve(entry.target));
                this.renderAll();
            }, { threshold: 0.1 });
            this.diagrams.forEach((pre) => this.intersectionObserver?.observe(pre));
            this.themeObserver = new MutationObserver((mutations) => {
                if (!mutations.some((m) => m.attributeName === 'data-bs-theme'))
                    return;
                this.initializeMermaid();
                this.renderAll(true);
            });
            this.themeObserver.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-bs-theme'],
            });
        }
        catch (error) {
            Logger.error('Failed to load Mermaid for the docs viewer', error);
        }
    }
    disconnect() {
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = null;
        this.themeObserver?.disconnect();
        this.themeObserver = null;
        document.removeEventListener('keydown', this.handleEscape);
        this.modalBackdrop?.remove();
        this.modalBackdrop = null;
        this.modalBody = null;
    }
    isDarkTheme() {
        return document.documentElement.getAttribute('data-bs-theme') === 'dark';
    }
    initializeMermaid() {
        if (!this.mermaid)
            return;
        this.mermaid.initialize({
            startOnLoad: false,
            theme: this.isDarkTheme() ? 'dark' : 'default',
            securityLevel: 'strict',
        });
    }
    // Wraps each ```mermaid fence in a toolbar + canvas shell (see _docs.scss's .doc-mermaid-*
    // rules) and stashes its source text before mermaid ever overwrites the <pre> with an SVG.
    wrapDiagrams() {
        const codeBlocks = Array.from(this.element.querySelectorAll('pre > code.language-mermaid'));
        return codeBlocks.map((code) => {
            const pre = code.parentElement;
            const source = code.textContent || '';
            const wrapper = document.createElement('div');
            wrapper.className = 'doc-mermaid';
            const toolbar = document.createElement('div');
            toolbar.className = 'doc-mermaid-toolbar';
            toolbar.append(this.buildButton('+ Zoom In', () => this.zoom(pre, 0.15)), this.buildButton('− Zoom Out', () => this.zoom(pre, -0.15)), this.buildButton('↺ Reset', () => this.resetZoom(pre)), this.buildButton('⛶ Fullscreen', () => this.openFullscreen(pre)));
            const canvas = document.createElement('div');
            canvas.className = 'doc-mermaid-canvas';
            pre.replaceWith(wrapper);
            pre.classList.add('mermaid');
            pre.textContent = source; // drop the <code> wrapper's own HTML escaping
            pre.dataset.mermaidSource = source;
            canvas.appendChild(pre);
            wrapper.append(toolbar, canvas);
            return pre;
        });
    }
    buildButton(label, onClick) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn-sm btn-outline-secondary';
        btn.textContent = label;
        btn.addEventListener('click', onClick);
        return btn;
    }
    // force re-renders every diagram from its stashed source (a theme change) rather than only the
    // ones not yet rendered (the initial, intersection-gated render).
    renderAll(force = false) {
        if (!this.mermaid)
            return;
        const targets = force ? this.diagrams : this.diagrams.filter((pre) => !pre.querySelector('svg'));
        if (targets.length === 0)
            return;
        if (force) {
            targets.forEach((pre) => {
                pre.textContent = pre.dataset.mermaidSource || '';
            });
        }
        this.mermaid
            .run({ nodes: targets })
            .then(() => {
            targets.forEach((pre) => {
                const svg = pre.querySelector('svg');
                if (svg) {
                    svg.style.maxWidth = '100%';
                    svg.style.width = '100%';
                    svg.style.height = 'auto';
                }
            });
            // The open fullscreen clone was rendered under the old theme — refresh it from the
            // original, now re-rendered.
            if (force && this.fullscreenSource)
                this.openFullscreen(this.fullscreenSource);
        })
            .catch((error) => Logger.error('Failed to render a docs Mermaid diagram', error));
    }
    zoom(pre, factor) {
        const level = Math.min(3.0, Math.max(0.4, (this.zoomLevels.get(pre) || 1.0) + factor));
        this.zoomLevels.set(pre, level);
        pre.style.transform = `scale(${level})`;
    }
    resetZoom(pre) {
        this.zoomLevels.set(pre, 1.0);
        pre.style.transform = 'scale(1.0)';
    }
    ensureModal() {
        if (this.modalBackdrop && this.modalBody) {
            return { backdrop: this.modalBackdrop, body: this.modalBody };
        }
        const backdrop = document.createElement('div');
        backdrop.className = 'doc-mermaid-modal-backdrop';
        backdrop.addEventListener('click', (event) => {
            if (event.target === backdrop)
                this.closeFullscreen();
        });
        const dialog = document.createElement('div');
        dialog.className = 'doc-mermaid-modal';
        const header = document.createElement('div');
        header.className = 'doc-mermaid-modal-header';
        const title = document.createElement('h3');
        title.textContent = 'Diagram — Fullscreen View';
        const actions = document.createElement('div');
        actions.className = 'doc-mermaid-modal-actions';
        actions.append(this.buildButton('+ Zoom In', () => this.zoomModal(0.2)), this.buildButton('− Zoom Out', () => this.zoomModal(-0.2)), this.buildButton('↺ Reset', () => this.resetModalZoom()), this.buildButton('✕ Close (Esc)', () => this.closeFullscreen()));
        header.append(title, actions);
        const body = document.createElement('div');
        body.className = 'doc-mermaid-modal-body';
        dialog.append(header, body);
        backdrop.appendChild(dialog);
        document.body.appendChild(backdrop);
        document.addEventListener('keydown', this.handleEscape);
        this.modalBackdrop = backdrop;
        this.modalBody = body;
        return { backdrop, body };
    }
    openFullscreen(pre) {
        const { backdrop, body } = this.ensureModal();
        body.innerHTML = '';
        const clone = pre.cloneNode(true);
        clone.style.transform = 'scale(1.0)';
        this.modalScale = 1.0;
        body.appendChild(clone);
        this.fullscreenSource = pre;
        backdrop.classList.add('active');
        document.body.style.overflow = 'hidden';
    }
    closeFullscreen() {
        if (!this.modalBackdrop)
            return;
        this.modalBackdrop.classList.remove('active');
        document.body.style.overflow = '';
        this.fullscreenSource = null;
    }
    zoomModal(factor) {
        const content = this.modalBody?.firstElementChild;
        if (!content)
            return;
        this.modalScale = Math.min(4.0, Math.max(0.3, this.modalScale + factor));
        content.style.transform = `scale(${this.modalScale})`;
    }
    resetModalZoom() {
        const content = this.modalBody?.firstElementChild;
        if (!content)
            return;
        this.modalScale = 1.0;
        content.style.transform = 'scale(1.0)';
    }
}
